import React from 'react';
import { TemaCard } from 'src/data/models/TemaCardModel';
import { Card, Typography, Row, Col, Tag } from 'antd';
import MiniWordCard from './MiniWordCard';
import { MathNumberGroup } from 'src/data/models/NumberModel';
import NumberGroupList from './NumbersCard';

const { Paragraph, Text } = Typography;

interface CardListProps {
  cards: (TemaCard | MathNumberGroup)[];
  filterTopic?: string;
}

const typeColors: Record<string, string> = {
  word: 'green',
  phrase: '#00B8A9',
  sentence: '#efabff',
  idiom: '#b399ff',
  conjunction: 'orange',
};

// Type guards
const isTemaCard = (card: any): card is TemaCard =>
  typeof card === 'object' && 'type' in card;

const isMathNumberGroup = (card: any): card is MathNumberGroup =>
  typeof card === 'object' && 'numbers' in card && Array.isArray(card.numbers);

const CardList: React.FC<CardListProps> = ({ cards, filterTopic }) => {
  const filteredCards = filterTopic
    ? cards.filter((card) =>
      isTemaCard(card) ? card.topic === filterTopic : false
    )
    : cards;

  return (
    <>
      <Row gutter={[16, 16]}>
        {filteredCards.map((card, index) => {
          // 1. Word Card
          if (isTemaCard(card) && card.type === 'word') {
            return (
              <Col key={card.id} xs={24} sm={12} md={8} lg={6}>
                <MiniWordCard card={card} />
              </Col>
            );
          }

          // 2. Other TemaCards (phrase, idiom, etc.)
          if (isTemaCard(card) && card.type !== 'number') {
            return (
              <Col key={card.id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  title={
                    <Text
                      style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
                      strong
                    >
                      {card.spanish}
                    </Text>
                  }
                  style={{
                    borderLeft: `6px solid ${typeColors[card.type] || '#ccc'}`,
                  }}
                >
                  <Paragraph style={{ marginBottom: 8 }}>
                    <Text strong>English:</Text> {card.english}
                  </Paragraph>
                  <Paragraph style={{ marginBottom: 8 }}>
                    <Text strong>Type:</Text>{' '}
                    <Tag color={typeColors[card.type]}>{card.type}</Tag>
                  </Paragraph>
                  {card.notes && (
                    <Paragraph style={{ fontStyle: 'italic', color: '#555' }}>
                      <Text type="secondary">{card.notes}</Text>
                    </Paragraph>
                  )}
                  {card.example && (
                    <Paragraph>
                      <Text>
                        <Text strong>Ex: </Text>
                        {card.example}
                      </Text>
                    </Paragraph>
                  )}
                </Card>
              </Col>
            );
          }

          return null;
        })}
      </Row>

      {/* 3. Math Numbers */}
      {filteredCards.some(isMathNumberGroup) && (
        <NumberGroupList key="number-groups" groups={filteredCards.filter(isMathNumberGroup)} />
      )}
    </>
  );
};

export default CardList;
